import PropTypes from "prop-types";
import { Box, Checkbox, Select, Textarea, TextInput } from "../../../themeV2";
import { centsToDollars, NEW_GROUP } from "./serviceForm";
import { placeholdersFor } from "./serviceTypes";
import s from "./ManageServices.module.css";

// Both answer "what can one shopper end up with", so they are asked together.
const BASKET_FIELDS = Object.freeze(["maxQuantity", "exclusionGroup"]);

const BASKET_GROUP = Object.freeze({
  legend: "Quantity and combinations",
  intro:
    "How many of this a shopper can add to one product, and whether it can be bought alongside your other services.",
});

const DESCRIPTIONS = Object.freeze({
  price:
    "What you keep from each order. Streamily adds its fee on top, so the shopper pays a little more.",
  label: "What the shopper sees on the product page.",
  maxWordCount:
    "The most words a shopper may ask for. Leave blank for no limit.",
  requiresTextInput:
    "Tick when you need words from the shopper — a name to write, or what to draw. Untick for a stamp that needs nothing typed.",
  maxQuantity:
    "Per product. Set 3 and a shopper can add three of these to the same item.",
  exclusionGroup:
    "Services sharing a group name cannot be bought together — put “Short Quote” and “Long Quote” in one group and a shopper picks just one.",
});

export default function ServiceFormFields({
  category,
  fields,
  service = null,
  isLoading,
  errorFor,
  clearFieldError,
  requiresText,
  onRequiresTextChange,
  exclusionGroup,
  onExclusionGroupChange,
  existingGroups,
  exclusionGroupItems,
}) {
  const placeholders = placeholdersFor(category);

  const renderers = {
    price: () => (
      <TextInput
        name="price"
        label="Your Earnings ($)"
        required
        disabled={isLoading}
        placeholder="e.g. 5.00"
        description={DESCRIPTIONS.price}
        error={errorFor("price")}
        onValueChange={clearFieldError("price")}
        defaultValue={service ? centsToDollars(service.splitAmount) : undefined}
      />
    ),

    label: () => (
      <TextInput
        name="label"
        label="Label"
        required
        disabled={isLoading}
        placeholder={`e.g. ${placeholders.label}`}
        description={DESCRIPTIONS.label}
        error={errorFor("label")}
        onValueChange={clearFieldError("label")}
        defaultValue={service ? service.label : undefined}
      />
    ),

    description: () => (
      <Textarea
        name="description"
        label="Description (optional)"
        rows={2}
        disabled={isLoading}
        placeholder={`e.g. ${placeholders.description}`}
        defaultValue={service ? service.description : undefined}
      />
    ),

    maxWordCount: () => (
      <TextInput
        name="maxWordCount"
        label="Word limit (optional)"
        disabled={isLoading}
        placeholder={`e.g. ${placeholders.maxWordCount}`}
        description={DESCRIPTIONS.maxWordCount}
        error={errorFor("maxWordCount")}
        onValueChange={clearFieldError("maxWordCount")}
        defaultValue={
          service?.maxWordCount ? String(service.maxWordCount) : undefined
        }
      />
    ),

    requiresTextInput: () => (
      <Checkbox
        name="requiresTextInput"
        label="The shopper must type something"
        description={DESCRIPTIONS.requiresTextInput}
        checked={requiresText}
        onCheckedChange={onRequiresTextChange}
        disabled={isLoading}
      />
    ),

    maxQuantity: () => (
      <TextInput
        name="maxQuantity"
        label="How many times can one shopper buy this?"
        disabled={isLoading}
        description={DESCRIPTIONS.maxQuantity}
        error={errorFor("maxQuantity")}
        onValueChange={clearFieldError("maxQuantity")}
        defaultValue={service ? String(service.maxQuantity) : "1"}
      />
    ),

    /* Groups are matched by exact string across the product page, the cart and
       the server, so free text let a stray capital or trailing space silently
       disable the constraint. */
    exclusionGroup: () => (
      <>
        <Select.Root
          name="exclusionGroup"
          label="Only one of these"
          disabled={isLoading}
          value={exclusionGroup}
          onValueChange={(next) => {
            clearFieldError("exclusionGroup")();
            onExclusionGroupChange(next);
          }}
          items={exclusionGroupItems}
          description={DESCRIPTIONS.exclusionGroup}
          error={errorFor("exclusionGroup")}
        >
          <Select.Item value="" label="None">
            None
          </Select.Item>
          {existingGroups.map((group) => (
            <Select.Item key={group} value={group} label={group}>
              {group}
            </Select.Item>
          ))}
          <Select.Item value={NEW_GROUP} label="Create a new group…">
            Create a new group…
          </Select.Item>
        </Select.Root>

        {exclusionGroup === NEW_GROUP && (
          <TextInput
            name="newExclusionGroup"
            label="New group name"
            disabled={isLoading}
            autoFocus
            placeholder="e.g. Quote"
          />
        )}
      </>
    ),
  };

  const basketFields = fields.filter((field) => BASKET_FIELDS.includes(field));
  const ownFields = fields.filter((field) => !BASKET_FIELDS.includes(field));

  return (
    <Box direction="column" gap={4}>
      {ownFields.map((field) => (
        <div key={field}>{renderers[field]()}</div>
      ))}

      {basketFields.length > 0 && (
        <fieldset className={s.fieldGroup}>
          <legend className={s.fieldGroupLegend}>{BASKET_GROUP.legend}</legend>
          <p className={s.fieldGroupIntro}>{BASKET_GROUP.intro}</p>
          <Box direction="column" gap={4}>
            {basketFields.map((field) => (
              <div key={field}>{renderers[field]()}</div>
            ))}
          </Box>
        </fieldset>
      )}
    </Box>
  );
}

ServiceFormFields.propTypes = {
  category: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  service: PropTypes.shape({
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    splitAmount: PropTypes.number.isRequired,
    maxWordCount: PropTypes.number,
    maxQuantity: PropTypes.number,
  }),
  isLoading: PropTypes.bool.isRequired,
  errorFor: PropTypes.func.isRequired,
  clearFieldError: PropTypes.func.isRequired,
  requiresText: PropTypes.bool.isRequired,
  onRequiresTextChange: PropTypes.func.isRequired,
  exclusionGroup: PropTypes.string.isRequired,
  onExclusionGroupChange: PropTypes.func.isRequired,
  existingGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
  exclusionGroupItems: PropTypes.object.isRequired,
};
